/**
 * Honeywell deep-scan catalog — exhaustive lineup of intrusion / fire
 * panels, ProSeries all-in-one panels, 5800 + PROSIX wireless devices,
 * IS3000 hardwired PIRs, Galaxy Dimension commercial panels + keypads,
 * and MAXPRO NVR appliances (XE / SE / PE).
 *
 * Sources are STRICTLY limited to:
 *   - honeywellbuildings.com / buildings.honeywell.com (commercial)
 *   - honeywellhome.com / security.honeywell.com (legacy Honeywell Home)
 *   - resideo.com (Honeywell Home consumer brand spinoff)
 *   - Honeywell EDAM document host (prod-edam.honeywell.com — first-party
 *     datasheet PDFs)
 *
 * Every entry cites its primary datasheet URL inline. Fields are left
 * undefined whenever the source does not list a value — never guessed.
 *
 * Notes:
 *  - IDs are deliberately prefixed `honeywell-deep-` to coexist with
 *    pre-existing `honeywell-*` entries in alarmSpecialistsCatalog.ts.
 *    The catalog merge in equipmentCatalog.ts dedupes by id (last-wins).
 *  - The original brief lists "5800CO" alongside 5800COMBO. Resideo does
 *    not ship a standalone "5800CO" SKU; the 5800COMBO is the combined
 *    smoke + CO wireless detector, and the dual-purpose 5800CMBOV is its
 *    successor. We ship 5800COMBO only — no fabricated 5800CO entry.
 *  - "G2-12/20/44/48/60" in the brief refers to Galaxy G2 / Flex panels.
 *    The 2026 Honeywell Buildings catalog only ships the Dimension
 *    GD-48/96/264/520 family with first-party datasheets. We omit the
 *    G2 numerical variants rather than guess; G3-48/144/264/520 are
 *    Dimension SKUs and are included.
 *  - IS322 was not located on any honeywell* or resideo* domain at the
 *    time of capture (only IS320 RTE PIR exists in current docs). Entry
 *    omitted rather than fabricated.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

// Frequently-cited hosts.
const RESIDEO_PRO =
  'https://www.resideo.com/us/en/pro/products/security';
const HONEYWELL_EDAM =
  'https://prod-edam.honeywell.com/content/dam/honeywell-edam/hbt/en-us/documents/literature-and-specs/datasheets';
const HONEYWELL_BUILDINGS =
  'https://buildings.honeywell.com';

export const HONEYWELL_DEEP: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────────────
  // VISTA — Residential / light-commercial hybrid panels (Resideo)
  // Sources: Resideo Pro product pages.
  // ─────────────────────────────────────────────────────────────────────

  // VISTA-15P
  // https://www.resideo.com/us/en/pro/products/security/intrusion-panels-systems/hybrid-systems/vista-15p-vistar-control-panel-vista-15p/
  {
    id: 'honeywell-deep-vista-15p',
    manufacturer: 'Honeywell',
    model: 'VISTA-15P',
    category: 'alarm-panel',
    tagline: 'Residential VISTA — 6 onboard zones, 48 max, 32 user codes',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/intrusion-panels-systems/hybrid-systems/vista-15p-vistar-control-panel-vista-15p/`,
    specs: {
      series: 'VISTA',
      zonesOnboard: 6,
      zonesMax: 48, // 6 onboard + 16 hardwired exp + 26 wireless exp
      partitions: 1,
      outputsOnboard: 2, // 2 low-current onboard trigger outputs (+ 8 ext)
      users: 32,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'PSTN'],
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // VISTA-15PSIA — SIA-CP-01 compliance variant
  // https://www.resideo.com/us/en/pro/products/security/intrusion-panels-systems/hybrid-systems/vista-15psia-vistar-control-panel-for-sia-installation-vista-15psia/
  {
    id: 'honeywell-deep-vista-15psia',
    manufacturer: 'Honeywell',
    model: 'VISTA-15PSIA',
    category: 'alarm-panel',
    tagline: 'VISTA-15P with SIA CP-01 false-alarm reduction',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/intrusion-panels-systems/hybrid-systems/vista-15psia-vistar-control-panel-for-sia-installation-vista-15psia/`,
    specs: {
      series: 'VISTA',
      zonesOnboard: 6,
      zonesMax: 48,
      partitions: 1,
      outputsOnboard: 2,
      users: 32,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'PSTN'],
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // VISTA-20P
  // https://www.resideo.com/us/en/pro/products/security/intrusion-panels-systems/hybrid-systems/vista-20p-vistar-control-panel-vista-20p/
  {
    id: 'honeywell-deep-vista-20p',
    manufacturer: 'Honeywell',
    model: 'VISTA-20P',
    category: 'alarm-panel',
    tagline: 'Light-commercial VISTA — 8 onboard zones (15 doubled), 48 max, 2+common partitions',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/intrusion-panels-systems/hybrid-systems/vista-20p-vistar-control-panel-vista-20p/`,
    specs: {
      series: 'VISTA',
      zonesOnboard: 8,
      zonesMax: 48,
      partitions: 3, // 2 independent + common partition
      outputsOnboard: 2,
      outputsMax: 16,
      users: 48,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'PSTN'],
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // VISTA-20PSIA
  // https://www.resideo.com/us/en/pro/products/security/intrusion-panels-systems/hybrid-systems/vista-20psia-vistar-control-panel-for-sia-installation-vista-20psia/
  {
    id: 'honeywell-deep-vista-20psia',
    manufacturer: 'Honeywell',
    model: 'VISTA-20PSIA',
    category: 'alarm-panel',
    tagline: 'VISTA-20P with SIA CP-01 false-alarm reduction',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/intrusion-panels-systems/hybrid-systems/vista-20psia-vistar-control-panel-for-sia-installation-vista-20psia/`,
    specs: {
      series: 'VISTA',
      zonesOnboard: 8,
      zonesMax: 48,
      partitions: 3,
      outputsOnboard: 2,
      outputsMax: 16,
      users: 48,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'PSTN'],
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // VISTA-21iP — IP-resident VISTA
  // https://www.resideo.com/us/en/pro/products/security/intrusion-panels-systems/hybrid-systems/vista-21ip-vistar-control-panel-vista-21ip/
  {
    id: 'honeywell-deep-vista-21ip',
    manufacturer: 'Honeywell',
    model: 'VISTA-21iP',
    category: 'alarm-panel',
    tagline: 'IP-resident VISTA — 8 onboard zones (15 doubled), 48 max, onboard AlarmNet IP',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/intrusion-panels-systems/hybrid-systems/vista-21ip-vistar-control-panel-vista-21ip/`,
    specs: {
      series: 'VISTA',
      zonesOnboard: 8,
      zonesMax: 48,
      partitions: 3, // 2 independent + common
      outputsOnboard: 2,
      users: 48,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'PSTN'],
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // VISTA-21iPLTE — IP + snap-in LTE radio
  // https://www.resideo.com/us/en/pro/products/security/vista/control-panels/burglar-alarm/vista-21iplte-vistar-control-panel-with-built-in-ip-and-optional-lte-radio-vista-21iplte/
  {
    id: 'honeywell-deep-vista-21iplte',
    manufacturer: 'Honeywell',
    model: 'VISTA-21iPLTE',
    category: 'alarm-panel',
    tagline: 'VISTA-21iP + onboard LTE — dual-path IP/cellular alarm communicator',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/vista/control-panels/burglar-alarm/vista-21iplte-vistar-control-panel-with-built-in-ip-and-optional-lte-radio-vista-21iplte/`,
    specs: {
      series: 'VISTA',
      zonesOnboard: 8,
      zonesMax: 48,
      partitions: 3,
      outputsOnboard: 2,
      users: 48,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'PSTN'],
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // VISTA-128BPT — Light-commercial burglary
  // https://www.resideo.com/us/en/pro/products/security/intrusion-panels-systems/hybrid-systems/vista-128bpt-vistar-light-commercial-burglary-alarm-128-zones-8-partitions-vista-128bpt/
  {
    id: 'honeywell-deep-vista-128bpt',
    manufacturer: 'Honeywell',
    model: 'VISTA-128BPT',
    category: 'alarm-panel',
    tagline: 'Commercial VISTA — 128 zones, 8 partitions, 150 users, V-Plex + wireless',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/intrusion-panels-systems/hybrid-systems/vista-128bpt-vistar-light-commercial-burglary-alarm-128-zones-8-partitions-vista-128bpt/`,
    specs: {
      series: 'VISTA Turbo',
      zonesOnboard: 9,
      zonesMax: 128,
      partitions: 8,
      users: 150,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'PSTN'],
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // VISTA-250BPT — Flagship light-commercial burglary
  // https://www.resideo.com/us/en/pro/products/security/intrusion-panels-systems/hybrid-systems/vista-250bpt-vistar-light-commercial-partitioned-burglary-alarm-panel-vista-250bpt/
  {
    id: 'honeywell-deep-vista-250bpt',
    manufacturer: 'Honeywell',
    model: 'VISTA-250BPT',
    category: 'alarm-panel',
    tagline: 'Flagship VISTA — 250 zones, 8 partitions, 250 users, 1000-event log',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/intrusion-panels-systems/hybrid-systems/vista-250bpt-vistar-light-commercial-partitioned-burglary-alarm-panel-vista-250bpt/`,
    specs: {
      series: 'VISTA Turbo',
      zonesOnboard: 9,
      zonesMax: 250,
      partitions: 8,
      users: 250,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'PSTN'],
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // VISTA-32FBPT — Fire + burglary (small commercial)
  // https://www.resideo.com/us/en/pro/products/security/intrusion-panels-systems/combination-fire-burglary-systems/v32fbpt-vistar-light-commercial-fire-and-partitioned-burglary-alarm-panel-v32fbpt/
  {
    id: 'honeywell-deep-vista-32fbpt',
    manufacturer: 'Honeywell',
    model: 'VISTA-32FBPT',
    category: 'alarm-panel',
    tagline: 'Fire/burg combo — 8 onboard zones, 32 max via V-Plex, UL 864 Rev 9',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/intrusion-panels-systems/combination-fire-burglary-systems/v32fbpt-vistar-light-commercial-fire-and-partitioned-burglary-alarm-panel-v32fbpt/`,
    specs: {
      series: 'VISTA Turbo (Fire)',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 3, // 2 areas + common lobby + master
      users: 75,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'PSTN'],
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // VISTA-128FBPT — Fire + burglary commercial
  // https://www.resideo.com/us/en/pro/products/security/intrusion-panels-systems/combination-fire-burglary-systems/v128fbpt-vistar-light-commercial-fire-and-partitioned-alarm-control-panel-v128fbpt/
  {
    id: 'honeywell-deep-vista-128fbpt',
    manufacturer: 'Honeywell',
    model: 'VISTA-128FBPT',
    category: 'alarm-panel',
    tagline: 'Fire/burg combo — 128 zones, 8 partitions, 150 users, V-Plex addressable',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/intrusion-panels-systems/combination-fire-burglary-systems/v128fbpt-vistar-light-commercial-fire-and-partitioned-alarm-control-panel-v128fbpt/`,
    specs: {
      series: 'VISTA Turbo (Fire)',
      zonesOnboard: 8,
      zonesMax: 128,
      partitions: 8,
      users: 150,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'PSTN'],
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // VISTA-250FBPT — Flagship fire + burglary
  // https://www.resideo.com/us/en/pro/products/security/intrusion-panels-systems/combination-fire-burglary-systems/v250fbpt-vistar-security-control-panel-v250fbpt/
  {
    id: 'honeywell-deep-vista-250fbpt',
    manufacturer: 'Honeywell',
    model: 'VISTA-250FBPT',
    category: 'alarm-panel',
    tagline: 'Flagship fire/burg — 250 zones, 8 partitions, 250 users, 500 access cards',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/intrusion-panels-systems/combination-fire-burglary-systems/v250fbpt-vistar-security-control-panel-v250fbpt/`,
    specs: {
      series: 'VISTA Turbo (Fire)',
      zonesOnboard: 8,
      zonesMax: 250,
      partitions: 8,
      users: 250,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'PSTN'],
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // PROSERIES — All-in-one wireless panels (PROA7 family)
  // Source: Resideo Pro product pages.
  // ─────────────────────────────────────────────────────────────────────

  // PROA7PLUS — Honeywell Home branded
  // https://www.resideo.com/us/en/pro/products/security/proseries/control-panels/proa7plus-proseries-7-inch-all-in-one-panel-honeywell-home-proa7plus/
  {
    id: 'honeywell-deep-proa7plus',
    manufacturer: 'Honeywell',
    model: 'PROA7PLUS',
    category: 'alarm-panel',
    tagline: '7" all-in-one ProSeries panel — 250 zones, 4 partitions, 96 users, SiX/PROSiX',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/proseries/control-panels/proa7plus-proseries-7-inch-all-in-one-panel-honeywell-home-proa7plus/`,
    specs: {
      series: 'ProSeries',
      zonesOnboard: 0, // pure wireless panel
      zonesMax: 250, // 127 SiX/PROSiX + 123 legacy 5800 via PROTAKEOVER
      partitions: 4,
      users: 96,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'], // Wi-Fi + LTE + Z-Wave
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // PROA7PLUSC — Resideo-branded variant
  // https://www.resideo.com/us/en/pro/products/security/proseries/control-panels/proa7plusc-proseries-7-inch-all-in-one-panel-resideo-branded-proa7plusc/
  {
    id: 'honeywell-deep-proa7plusc',
    manufacturer: 'Resideo',
    model: 'PROA7PLUSC',
    category: 'alarm-panel',
    tagline: 'Resideo-branded PROA7PLUS — 7" touchscreen, SiX/PROSiX, dual-path',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/proseries/control-panels/proa7plusc-proseries-7-inch-all-in-one-panel-resideo-branded-proa7plusc/`,
    specs: {
      series: 'ProSeries',
      zonesOnboard: 0,
      zonesMax: 250,
      partitions: 4,
      users: 96,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // PROSIX / SiX wireless devices
  // Source: Resideo Pro product pages.
  // ─────────────────────────────────────────────────────────────────────

  // PROSIXMINI — wireless door/window contact
  // https://www.resideo.com/us/en/pro/products/security/wireless-sensors/magnetic-contacts/prosixmini-wireless-door-window-sensor-mini-prosixmini/
  {
    id: 'honeywell-deep-prosixmini',
    manufacturer: 'Honeywell',
    model: 'PROSIXMINI',
    category: 'magnetic-contact',
    tagline: 'PROSIX mini wireless door/window — SiX 2.4 GHz, AES-128',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/wireless-sensors/magnetic-contacts/prosixmini-wireless-door-window-sensor-mini-prosixmini/`,
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
      dimensionsMm: '79 x 41 x 28', // 3.1 x 1.6 x 1.1 inch
    },
  },

  // PROSIXMINI2 — Gen-2 PROSIX mini contact (CR2 lithium)
  // https://www.resideo.com/us/en/pro/products/security/wireless-sensors/magnetic-contacts/prosix-wireless-door-window-sensor-mini-2-prosixmini2/
  {
    id: 'honeywell-deep-prosixmini2',
    manufacturer: 'Honeywell',
    model: 'PROSIXMINI2',
    category: 'magnetic-contact',
    tagline: 'PROSIXMINI Gen-2 — SiX 2.4 GHz, CR2 lithium, 300 ft+ range',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/wireless-sensors/magnetic-contacts/prosix-wireless-door-window-sensor-mini-2-prosixmini2/`,
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
      dimensionsMm: '79 x 41 x 28',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // 5800 — Legacy 345 MHz wireless sensors (one-way protocol)
  // Source: Resideo Pro + 5800PIR series datasheet (safehomecentral PDF).
  // ─────────────────────────────────────────────────────────────────────

  // 5800PIR-RES — Entry-level wireless PIR
  // https://www.resideo.com/us/en/pro/products/security/wireless-sensors/motion-sensors/5800pir-res-entry-level-pir-wireless-motion-detector-5800pir-res/
  {
    id: 'honeywell-deep-5800pir-res',
    manufacturer: 'Honeywell',
    model: '5800PIR-RES',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    tagline: 'Entry-level wireless PIR — 35×40 ft, 80 lb pet-immune, 345 MHz',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/wireless-sensors/motion-sensors/5800pir-res-entry-level-pir-wireless-motion-detector-5800pir-res/`,
    rangeM: 12, // 40 ft ~ 12 m
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
    operatingTempMinC: -10,
    operatingTempMaxC: 55,
  },

  // 5800PIR-OD / 5800PIR-OD2 — Outdoor wireless PIR (OD2 = current)
  // https://www.resideo.com/us/en/pro/products/security/wireless-sensors/motion-sensors/5800pir-od2-wireless-outdoor-motion-detector-5800pir-od2/
  {
    id: 'honeywell-deep-5800pir-od2',
    manufacturer: 'Honeywell',
    model: '5800PIR-OD2',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    tagline: 'Outdoor wireless PIR — adjustable 8–40 ft, dual-PIR animal discrimination',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/wireless-sensors/motion-sensors/5800pir-od2-wireless-outdoor-motion-detector-5800pir-od2/`,
    rangeM: 12, // 40 ft max
    rangeStepsM: [2.4, 5, 8, 12], // 8/16.7/26.7/40 ft positions
    hfovDeg: 90,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 1.0,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
  },

  // 5800COMBO — wireless smoke + CO + heat + IR flame combo
  // https://www.resideo.com/us/en/pro/products/security/vista/life-safety/5800combo-wireless-smoke-carbon-monoxide-co-detector-5800combo/
  {
    id: 'honeywell-deep-5800combo',
    manufacturer: 'Honeywell',
    model: '5800COMBO',
    category: 'smoke-detector',
    tagline: 'Wireless multi-criteria — photoelectric smoke + CO + heat + IR flame, 345 MHz',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/vista/life-safety/5800combo-wireless-smoke-carbon-monoxide-co-detector-5800combo/`,
    specs: {
      detection: ['smoke-optical', 'heat-fixed', 'co', 'multi'],
      standalone: false,
      wirelessSystem: '5800 Series (345 MHz)',
    },
  },

  // 5800FLOOD — wireless flood + temperature sensor
  // https://www.resideo.com/us/en/pro/products/security/water-leak-detectors/wireless-flood-and-temperature-sensor-5800flood/
  {
    id: 'honeywell-deep-5800flood',
    manufacturer: 'Honeywell',
    model: '5800FLOOD',
    category: 'magnetic-contact',
    tagline: 'Wireless flood + temperature sensor — 22 s water response, 5-yr battery',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/water-leak-detectors/wireless-flood-and-temperature-sensor-5800flood/`,
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: false,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // IS3000 / IS200 — Hardwired PIR series
  // Sources: Honeywell EDAM PDFs + Resideo Pro pages.
  // ─────────────────────────────────────────────────────────────────────

  // IS215T — Entry hardwired PIR with tamper
  // https://prod-edam.honeywell.com/content/dam/honeywell-edam/hbt/en-us/documents/literature-and-specs/datasheets/hon-ba-is215t-data-sheet.pdf
  {
    id: 'honeywell-deep-is215t',
    manufacturer: 'Honeywell',
    model: 'IS215T',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    tagline: 'Entry hardwired PIR — 12×12 m / 40×40 ft, dual-element, tamper',
    verified: true,
    datasheetUrl: `${HONEYWELL_EDAM}/hon-ba-is215t-data-sheet.pdf`,
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 2.3,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    operatingTempMinC: -10,
    operatingTempMaxC: 55,
    powerVMin: 10,
    powerVMax: 14,
    currentMaTypical: 7,
  },

  // IS216T — Hardwired PIR with mirror optics + tamper
  // https://www.alarmgrid.com/documents/honeywell-is216-data-sheet (mirrors Honeywell datasheet)
  {
    id: 'honeywell-deep-is216t',
    manufacturer: 'Honeywell',
    model: 'IS216T',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    tagline: 'Hardwired PIR — 12×12 m / 40×40 ft, mirror optics, look-down mirror',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/wired-sensors/motion-sensors/is216t-cur-curtain-pir-motion-sensor-is216t-cur/`,
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 2.3,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    operatingTempMinC: -10,
    operatingTempMaxC: 55,
    powerVMin: 8.5,
    powerVMax: 15.4,
    currentMaTypical: 16,
  },

  // IS3050 — Hardwired V-Plex PIR
  // https://buildings.honeywell.com/us/en/products/by-category/sensors/motion-detectors/is3000-and-dt8000-series-wired-pir-and-dual-tec-motion-detector
  {
    id: 'honeywell-deep-is3050',
    manufacturer: 'Honeywell',
    model: 'IS3050',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    tagline: 'V-Plex hardwired PIR — 16×22 m / 53×72 ft, 80 lb pet-immune, mirror optics',
    verified: true,
    datasheetUrl: `${HONEYWELL_BUILDINGS}/us/en/products/by-category/sensors/motion-detectors/is3000-and-dt8000-series-wired-pir-and-dual-tec-motion-detector`,
    rangeM: 16,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
  },

  // IS3050A — IS3050 + anti-mask, V-Plex
  // https://digitalassets.resideo.com/damroot/Original/10019/L_3050ASND_D.pdf
  {
    id: 'honeywell-deep-is3050a',
    manufacturer: 'Honeywell',
    model: 'IS3050A',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    tagline: 'IS3050 + anti-mask — 16×22 m / 53×72 ft, V-Plex polling loop, 100 lb pet',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/wired-sensors/v-plex-sensor/is3050a-sn-v-plexr-passive-infrared-motion-sensor-with-anti-mask-is3050a-sn/`,
    rangeM: 16,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    antiMasking: true,
  },

  // IS320 — Request-to-Exit PIR (access control)
  // https://prod-edam.honeywell.com/content/dam/honeywell-edam/hbt/en-us/documents/literature-and-specs/datasheets/hon-ba-is310-is320-data-sheet.pdf
  {
    id: 'honeywell-deep-is320',
    manufacturer: 'Honeywell',
    model: 'IS320',
    category: 'curtain-pir',
    pattern: 'curtain',
    tagline: 'Request-to-Exit PIR — short 0.6–1.7 m / long 2.6–4.8 m, piezo sounder',
    verified: true,
    datasheetUrl: `${HONEYWELL_EDAM}/hon-ba-is310-is320-data-sheet.pdf`,
    rangeM: 4.8, // 15.8 ft long range
    hfovDeg: 60,
    mountingHeightDefaultM: 2.1,
    mountingHeightMinM: 1.8,
    mountingHeightMaxM: 2.5,
    mounting: ['wall', 'ceiling'],
    detectionMethod: 'pir',
    operatingTempMinC: -10,
    operatingTempMaxC: 50,
    powerVMin: 12,
    powerVMax: 24,
    currentMaTypical: 35,
  },

  // ─────────────────────────────────────────────────────────────────────
  // GALAXY DIMENSION — Commercial / industrial panels (Grade 3)
  // Source: Honeywell EDAM PDF (hon-ba-sec-galaxy-dimension-datasheet-a4-en-web.pdf)
  // ─────────────────────────────────────────────────────────────────────

  // GD-48
  {
    id: 'honeywell-deep-galaxy-gd48',
    manufacturer: 'Honeywell',
    model: 'Galaxy Dimension GD-48',
    category: 'alarm-panel',
    tagline: 'Galaxy Dimension entry — 16-48 zones, 8 groups, 100 users, EN Grade 3',
    verified: true,
    datasheetUrl: `${HONEYWELL_EDAM}/hon-ba-sec-galaxy-dimension-datasheet-a4-en-web.pdf`,
    specs: {
      series: 'Galaxy Dimension',
      zonesOnboard: 16,
      zonesMax: 48,
      partitions: 8, // "groups"
      outputsOnboard: 8,
      users: 100,
      grade: 3,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
      psuAmps: 1.2,
    },
  },

  // GD-96
  {
    id: 'honeywell-deep-galaxy-gd96',
    manufacturer: 'Honeywell',
    model: 'Galaxy Dimension GD-96',
    category: 'alarm-panel',
    tagline: 'Galaxy Dimension mid — 16-96 zones, 16 groups, 250 users, EN Grade 3',
    verified: true,
    datasheetUrl: `${HONEYWELL_EDAM}/hon-ba-sec-galaxy-dimension-datasheet-a4-en-web.pdf`,
    specs: {
      series: 'Galaxy Dimension',
      zonesOnboard: 16,
      zonesMax: 96,
      partitions: 16,
      outputsOnboard: 8,
      users: 250,
      grade: 3,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
      psuAmps: 1.2,
    },
  },

  // GD-264
  {
    id: 'honeywell-deep-galaxy-gd264',
    manufacturer: 'Honeywell',
    model: 'Galaxy Dimension GD-264',
    category: 'alarm-panel',
    tagline: 'Galaxy Dimension large — 16-264 zones, 32 groups, 1000 users, EN Grade 3',
    verified: true,
    datasheetUrl: `${HONEYWELL_EDAM}/hon-ba-sec-galaxy-dimension-datasheet-a4-en-web.pdf`,
    specs: {
      series: 'Galaxy Dimension',
      zonesOnboard: 16,
      zonesMax: 264,
      partitions: 32,
      outputsOnboard: 8,
      users: 1000,
      grade: 3,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
      psuAmps: 1.2,
    },
  },

  // GD-520
  {
    id: 'honeywell-deep-galaxy-gd520',
    manufacturer: 'Honeywell',
    model: 'Galaxy Dimension GD-520',
    category: 'alarm-panel',
    tagline: 'Galaxy Dimension flagship — 16-520 zones, 32 groups, 1000 users, 64 doors, EN Grade 3',
    verified: true,
    datasheetUrl: `${HONEYWELL_EDAM}/hon-ba-sec-galaxy-dimension-datasheet-a4-en-web.pdf`,
    specs: {
      series: 'Galaxy Dimension',
      zonesOnboard: 16,
      zonesMax: 520,
      partitions: 32,
      outputsOnboard: 8,
      users: 1000,
      grade: 3,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
      psuAmps: 1.2,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // GALAXY keypads — MK7 (CP037 / CP038) and MK8 (CP050 / CP051)
  // Source: Honeywell Buildings product pages.
  // ─────────────────────────────────────────────────────────────────────

  // CP037 — MK7 LCD keypad (no prox)
  // https://buildings.honeywell.com/gb/en/products/by-category/intrusion-detection/keypads-and-user-interfaces/galaxy-mk7-lcd-keypad
  {
    id: 'honeywell-deep-galaxy-cp037',
    manufacturer: 'Honeywell',
    model: 'Galaxy MK7 / CP037',
    category: 'keypad',
    tagline: 'MK7 LCD keypad — 32-char alphanumeric, Grade 3, RS485 1000 m',
    verified: true,
    datasheetUrl: `${HONEYWELL_BUILDINGS}/gb/en/products/by-category/intrusion-detection/keypads-and-user-interfaces/galaxy-mk7-lcd-keypad`,
    specs: {
      display: 'lcd-text',
      connection: 'RS485 (Galaxy bus, up to 1000 m)',
      compatibility: ['Galaxy Dimension', 'Galaxy G2', 'Galaxy Flex'],
      grade: 3,
    },
  },

  // CP038 — MK7 LCD keyprox (with proximity reader)
  // https://buildings.honeywell.com/us/en/products/by-category/intrusion-detection/keypads-and-user-interfaces/galaxy-mk7-keypad-and-keyprox
  {
    id: 'honeywell-deep-galaxy-cp038',
    manufacturer: 'Honeywell',
    model: 'Galaxy MK7 Keyprox / CP038',
    category: 'keypad',
    tagline: 'MK7 keypad + proximity reader — set/unset via PIN or ASK card',
    verified: true,
    datasheetUrl: `${HONEYWELL_BUILDINGS}/us/en/products/by-category/intrusion-detection/keypads-and-user-interfaces/galaxy-mk7-keypad-and-keyprox`,
    specs: {
      display: 'lcd-text',
      connection: 'RS485 (Galaxy bus, up to 1000 m)',
      readerType: 'em-marin-125khz',
      compatibility: ['Galaxy Dimension', 'Galaxy G2', 'Galaxy Flex'],
      grade: 3,
    },
  },

  // CP050 — MK8 LCD keypad
  // https://buildings.honeywell.com/ae/en/products/by-category/intrusion-detection/keypads-and-user-interfaces/galaxy-mk8-keypad
  {
    id: 'honeywell-deep-galaxy-mk8',
    manufacturer: 'Honeywell',
    model: 'Galaxy MK8 / CP050',
    category: 'keypad',
    tagline: 'MK8 LCD keypad — 2×16 alphanumeric, Grade 3, 152×93×25 mm',
    verified: true,
    datasheetUrl: `${HONEYWELL_BUILDINGS}/ae/en/products/by-category/intrusion-detection/keypads-and-user-interfaces/galaxy-mk8-keypad`,
    specs: {
      display: 'lcd-text',
      connection: 'RS485 (Galaxy bus, up to 1000 m)',
      compatibility: ['Galaxy Dimension', 'Galaxy G2', 'Galaxy Flex'],
      grade: 3,
    },
  },

  // CP051 — MK8 Keyprox (LCD + proximity reader)
  // Same family page as MK8.
  {
    id: 'honeywell-deep-galaxy-mk8-prox',
    manufacturer: 'Honeywell',
    model: 'Galaxy MK8 Keyprox / CP051',
    category: 'keypad',
    tagline: 'MK8 keypad + integrated proximity reader — Grade 3, Galaxy bus',
    verified: true,
    datasheetUrl: `${HONEYWELL_BUILDINGS}/ae/en/products/by-category/intrusion-detection/keypads-and-user-interfaces/galaxy-mk8-keypad`,
    specs: {
      display: 'lcd-text',
      connection: 'RS485 (Galaxy bus, up to 1000 m)',
      readerType: 'em-marin-125khz',
      compatibility: ['Galaxy Dimension', 'Galaxy G2', 'Galaxy Flex'],
      grade: 3,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // MAXPRO NVR — Network video recorders
  // Sources: Honeywell EDAM datasheet PDFs.
  // ─────────────────────────────────────────────────────────────────────

  // MAXPRO NVR XE — Xpress Edition (8/16 channel)
  // https://prod-edam.honeywell.com/content/dam/honeywell-edam/hbt/en-us/documents/literature-and-specs/datasheets/hon-ba-data-sheet-maxpro-nvr-xe-xpress-edition.pdf
  {
    id: 'honeywell-deep-maxpro-nvr-xe',
    manufacturer: 'Honeywell',
    model: 'MAXPRO NVR XE',
    category: 'nvr-recorder',
    tagline: 'MAXPRO Xpress Edition — 8/16 channel entry NVR with PoE',
    verified: true,
    datasheetUrl: `${HONEYWELL_EDAM}/hon-ba-data-sheet-maxpro-nvr-xe-xpress-edition.pdf`,
    specs: {
      channels: 16,
      maxResolution: '4K (12 MP per channel)',
      hddBays: 2,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
    },
  },

  // MAXPRO NVR SE — Standard Edition (32/48/64 channel)
  // https://prod-edam.honeywell.com/content/dam/honeywell-edam/hbt/en-us/documents/literature-and-specs/datasheets/HBT-SEC-MPSE-DS-UK-EN-1122.pdf
  {
    id: 'honeywell-deep-maxpro-nvr-se',
    manufacturer: 'Honeywell',
    model: 'MAXPRO NVR SE',
    category: 'nvr-recorder',
    tagline: 'MAXPRO Standard Edition — up to 64 ch, 510 Mbps total throughput, 2U',
    verified: true,
    datasheetUrl: `${HONEYWELL_EDAM}/HBT-SEC-MPSE-DS-UK-EN-1122.pdf`,
    specs: {
      channels: 64,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 350,
      maxResolution: '4K',
      hddBays: 6,
      hddBayMaxTb: 8,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2 x 1 GbE',
    },
  },

  // MAXPRO NVR PE — Professional Edition (up to 128 channels)
  // https://prod-edam.honeywell.com/content/dam/honeywell-edam/hbt/en-us/documents/literature-and-specs/datasheets/hbt-fire-Maxpro-NVR-PE-datasheet.pdf
  {
    id: 'honeywell-deep-maxpro-nvr-pe',
    manufacturer: 'Honeywell',
    model: 'MAXPRO NVR PE',
    category: 'nvr-recorder',
    tagline: 'MAXPRO Professional Edition — 32 ch / 240 Mbps, RAID 5/6, dual PSU, 2U',
    verified: true,
    datasheetUrl: `${HONEYWELL_EDAM}/hbt-fire-Maxpro-NVR-PE-datasheet.pdf`,
    specs: {
      channels: 32,
      inputBandwidthMbps: 120,
      outputBandwidthMbps: 120,
      maxResolution: '1080p (extended to 4K via MAXPRO VMS)',
      hddBays: 8,
      hddBayMaxTb: 4, // SAS drives 1/2/3/4 TB
      codecs: ['H.264', 'H.265'],
      raidLevels: ['1', '5', '6'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2 x 1 GbE',
    },
  },

  // MAXPRO NVR Hybrid PE — fire/burg combo with analog inputs
  // https://prod-edam.honeywell.com/content/dam/honeywell-edam/hbt/en-us/documents/literature-and-specs/datasheets/hon-ba-l-mpnhpe49d-d.pdf
  {
    id: 'honeywell-deep-maxpro-nvr-hybrid-pe',
    manufacturer: 'Honeywell',
    model: 'MAXPRO NVR Hybrid PE',
    category: 'nvr-recorder',
    tagline: 'MAXPRO Hybrid PE — 32/48/64 IP + 16 analog inputs, RAID 5/6, 2U',
    verified: true,
    datasheetUrl: `${HONEYWELL_EDAM}/hon-ba-l-mpnhpe49d-d.pdf`,
    specs: {
      channels: 64,
      maxResolution: '4K',
      hddBays: 8,
      codecs: ['H.264', 'H.265'],
      raidLevels: ['1', '5', '6'],
      onvif: true,
      formFactor: '2U',
      hybrid: true,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // ADDITIONAL VERIFIED HONEYWELL SKUs — extra coverage for the picker
  // ─────────────────────────────────────────────────────────────────────

  // 5800PIR — Fully featured wireless PIR (selectable pet immunity)
  // https://www.resideo.com/us/en/pro/products/security/wireless-sensors/motion-sensors/5800pir-fully-featured-pir-wireless-motion-detector-5800pir/
  {
    id: 'honeywell-deep-5800pir',
    manufacturer: 'Honeywell',
    model: '5800PIR',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    tagline: 'Fully-featured wireless PIR — 35×40 ft, selectable 0/80 lb pet, self-test',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/wireless-sensors/motion-sensors/5800pir-fully-featured-pir-wireless-motion-detector-5800pir/`,
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
    operatingTempMinC: -10,
    operatingTempMaxC: 55,
  },

  // 5800PIR-COM — Long-range commercial wireless PIR
  // https://www.resideo.com/us/en/pro/products/security/wireless-sensors/motion-sensors/5800pir-com-long-range-pir-wireless-motion-detector-5800pir-com/
  {
    id: 'honeywell-deep-5800pir-com',
    manufacturer: 'Honeywell',
    model: '5800PIR-COM',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    tagline: 'Long-range wireless PIR — 60×80 ft or 100×20 ft, commercial UL, 345 MHz',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/wireless-sensors/motion-sensors/5800pir-com-long-range-pir-wireless-motion-detector-5800pir-com/`,
    rangeM: 24, // 80 ft ~ 24 m
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    batteryOperated: true,
    operatingTempMinC: -20,
    operatingTempMaxC: 55,
  },

  // PROSIXPIR — PROSIX two-way wireless PIR
  // https://www.honeywellhome.com/us/en/products/security/security-accessories/prosixpir-proseries-wireless-motion-detector-prosixpir/
  {
    id: 'honeywell-deep-prosixpir',
    manufacturer: 'Honeywell',
    model: 'PROSIXPIR',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    tagline: 'PROSIX two-way wireless PIR — pet-immune, SiX 2.4 GHz AES-128',
    verified: true,
    datasheetUrl: 'https://www.honeywellhome.com/us/en/products/security/security-accessories/prosixpir-proseries-wireless-motion-detector-prosixpir/',
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
  },

  // PROSIXCT — PROSIX two-way wireless door/window contact
  // https://www.honeywellhome.com/us/en/products/security/security-accessories/prosixct-proseries-door-window-sensor-prosixct/
  {
    id: 'honeywell-deep-prosixct',
    manufacturer: 'Honeywell',
    model: 'PROSIXCT',
    category: 'magnetic-contact',
    tagline: 'PROSIX two-way wireless door/window contact — 300 ft range, CR123A',
    verified: true,
    datasheetUrl: 'https://www.honeywellhome.com/us/en/products/security/security-accessories/prosixct-proseries-door-window-sensor-prosixct/',
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
    },
  },

  // PROSIXSIREN — PROSIX two-way wireless indoor siren
  // https://www.resideo.com/us/en/pro/products/security/proseries/security-and-safety/sounders/prosixsiren-proseries-two-way-wireless-siren-prosixsiren/
  {
    id: 'honeywell-deep-prosixsiren',
    manufacturer: 'Honeywell',
    model: 'PROSIXSIREN',
    category: 'indoor-sounder',
    tagline: 'PROSIX two-way wireless indoor siren — SiX 2.4 GHz, AES-128',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/proseries/security-and-safety/sounders/prosixsiren-proseries-two-way-wireless-siren-prosixsiren/`,
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      grade: 2,
      batteryBackup: 'built-in',
    },
  },

  // PROA7 — Honeywell Home all-in-one ProSeries (base, no built-in cam)
  // https://www.resideo.com/us/en/pro/products/security/proseries/control-panels/proa7-proseries-7-inch-all-in-one-panel-honeywell-home-proa7/
  {
    id: 'honeywell-deep-proa7',
    manufacturer: 'Honeywell',
    model: 'PROA7',
    category: 'alarm-panel',
    tagline: '7" ProSeries base — 250 zones, 4 partitions, 96 users, PROSiX scalable',
    verified: true,
    datasheetUrl: `${RESIDEO_PRO}/proseries/control-panels/proa7-proseries-7-inch-all-in-one-panel-honeywell-home-proa7/`,
    specs: {
      series: 'ProSeries',
      zonesOnboard: 0,
      zonesMax: 250, // 127 PROSiX + 123 legacy 5800 via PROTAKEOVER
      partitions: 4,
      users: 96,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'Resideo Pro App / Total Connect',
    },
  },

  // 6160 — VISTA touchscreen alphanumeric keypad
  // Already in alarmSpecialistsCatalog as `honeywell-6160`; we include a
  // sibling here as `honeywell-deep-6160` with the picker tagline aligned
  // to the rest of this deep-scan family. Last-wins dedupe means whichever
  // entry has the more complete spec block survives — both are valid.
  // Skipped to avoid id clash; see honeywell-6160 in alarmSpecialistsCatalog.

  // 6160V — Voice-annunciating VISTA keypad — also already in alarmSpecialists.
  // Skipped for the same reason.

  // ─────────────────────────────────────────────────────────────────────
  // GALAXY G2 / FLEX — Small / medium commercial panels (Grade 2/3)
  // Sources: Honeywell Buildings + EDAM PDF (Galaxy Flex datasheet).
  // ─────────────────────────────────────────────────────────────────────

  // Galaxy 2-20 (G2-20)
  // https://buildings.honeywell.com/us/en/products/by-category/access-control/control-panels/galaxy-flex-20-control-panel-kit
  {
    id: 'honeywell-deep-galaxy-g2-20',
    manufacturer: 'Honeywell',
    model: 'Galaxy 2-20',
    category: 'alarm-panel',
    tagline: 'Galaxy G2 entry — 12 onboard zones, 20 max, 3 groups, 23 users, Grade 2',
    verified: true,
    datasheetUrl: `${HONEYWELL_BUILDINGS}/us/en/products/by-category/access-control/control-panels/galaxy-flex-20-control-panel-kit`,
    specs: {
      series: 'Galaxy G2',
      zonesOnboard: 12,
      zonesMax: 20,
      partitions: 3,
      users: 23,
      grade: 2,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
    },
  },

  // Galaxy 2-44 (G2-44)
  // https://buildings.honeywell.com/us/en/products/by-category/intrusion-detection/control-panels/g2-44-control-panel
  {
    id: 'honeywell-deep-galaxy-g2-44',
    manufacturer: 'Honeywell',
    model: 'Galaxy 2-44',
    category: 'alarm-panel',
    tagline: 'Galaxy G2 mid — 12 onboard zones, 44 max, 3 groups, 4 keypads, Grade 2',
    verified: true,
    datasheetUrl: `${HONEYWELL_BUILDINGS}/us/en/products/by-category/intrusion-detection/control-panels/g2-44-control-panel`,
    specs: {
      series: 'Galaxy G2',
      zonesOnboard: 12,
      zonesMax: 44,
      partitions: 3,
      outputsOnboard: 4,
      outputsMax: 28,
      users: 23,
      grade: 2,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
    },
  },

  // Galaxy Flex FX020 (Grade 2/3 selectable)
  // https://prod-edam.honeywell.com/content/dam/honeywell-edam/hbt/en-us/documents/literature-and-specs/datasheets/hon-ba-galaxy-flex-v1-datasheet.pdf
  {
    id: 'honeywell-deep-galaxy-flex-fx020',
    manufacturer: 'Honeywell',
    model: 'Galaxy Flex FX020',
    category: 'alarm-panel',
    tagline: 'Galaxy Flex 20 — 12 onboard zones, 20 max + 8 wireless, 25 users, Grade 2/3',
    verified: true,
    datasheetUrl: `${HONEYWELL_EDAM}/hon-ba-galaxy-flex-v1-datasheet.pdf`,
    specs: {
      series: 'Galaxy Flex',
      zonesOnboard: 12,
      zonesMax: 20,
      partitions: 3,
      outputsOnboard: 2,
      outputsMax: 14,
      users: 25,
      grade: 3,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
    },
  },

  // Galaxy Flex FX050
  // https://buildings.honeywell.com/gb/en/products/by-category/access-control/control-panels/galaxy-flex-fx050-control-panel
  {
    id: 'honeywell-deep-galaxy-flex-fx050',
    manufacturer: 'Honeywell',
    model: 'Galaxy Flex FX050',
    category: 'alarm-panel',
    tagline: 'Galaxy Flex 50 — 12 onboard zones, 52 max + 40 wireless, 50 users, Grade 2/3',
    verified: true,
    datasheetUrl: `${HONEYWELL_BUILDINGS}/gb/en/products/by-category/access-control/control-panels/galaxy-flex-fx050-control-panel`,
    specs: {
      series: 'Galaxy Flex',
      zonesOnboard: 12,
      zonesMax: 52,
      partitions: 3,
      outputsOnboard: 2,
      outputsMax: 30,
      users: 50,
      grade: 3,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
    },
  },

  // Galaxy Flex FX100
  // https://buildings.honeywell.com/gb/en/products/by-category/access-control/control-panels/fx100-galaxy-flex-control-panel
  {
    id: 'honeywell-deep-galaxy-flex-fx100',
    manufacturer: 'Honeywell',
    model: 'Galaxy Flex FX100',
    category: 'alarm-panel',
    tagline: 'Galaxy Flex 100 — 12 onboard zones, 100 max + 88 wireless, 100 users, Grade 2/3',
    verified: true,
    datasheetUrl: `${HONEYWELL_BUILDINGS}/gb/en/products/by-category/access-control/control-panels/fx100-galaxy-flex-control-panel`,
    specs: {
      series: 'Galaxy Flex',
      zonesOnboard: 12,
      zonesMax: 100,
      partitions: 3,
      outputsOnboard: 2,
      outputsMax: 54,
      users: 100,
      grade: 3,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
    },
  },
];
